import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DEMO_ROOT, actionKey, apiPost, resetActionKey } from "@/shared/api";

/**
 * Мутации демо-счёта: запуск, остановка, сброс (доработки §3).
 *
 * Все три инвалидируют демо-ветку кэша целиком (`DEMO_ROOT`), а не
 * перечисляют ключи. Экранов в демо мало, и пропущенный ключ стоил бы
 * устаревшего виртуального баланса на глазах у пользователя. Реальную ветку
 * они не трогают — по той же причине, по которой на бэкенде у демо свои
 * таблицы: смешения быть не должно нигде, включая кэш.
 */

export interface DemoStartPayload {
  amount: string;
}

export function useStartDemoRobot(robotId: number) {
  const client = useQueryClient();
  // Ключ рождается на старт действия и включает сумму — ровно как у реальной
  // покупки: созданный внутри `mutationFn`, он менялся бы на каждом повторе
  // после таймаута, и бэкенд честно завёл бы вторую позицию.
  const keyFor = (amount: string) => `demo-start:${String(robotId)}:${amount}`;

  return useMutation({
    mutationFn: ({ amount }: DemoStartPayload) =>
      apiPost("/api/v1/demo/robots/{robot_id}/start", {
        params: { robot_id: robotId },
        body: { amount },
        idempotencyKey: actionKey(keyFor(amount)),
      }),
    onSuccess: (_result, { amount }) => {
      resetActionKey(keyFor(amount));
      void client.invalidateQueries({ queryKey: DEMO_ROOT });
    },
  });
}

/**
 * Остановка демо-позиции. Ключ идемпотентности не нужен и контрактом не
 * предусмотрен: повтор по уже остановленной позиции даёт 409, а не вторую
 * остановку.
 */
export function useStopDemoPosition(positionId: number) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiPost("/api/v1/demo/positions/{position_id}/stop", {
        params: { position_id: positionId },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: DEMO_ROOT });
    },
  });
}

/**
 * Сброс демо-счёта. Ключ обязателен: двойной тап по подтверждению не должен
 * дать два поколения счёта.
 */
export function useResetDemo() {
  const client = useQueryClient();
  const key = "demo-reset";

  return useMutation({
    mutationFn: () =>
      apiPost("/api/v1/demo/reset", { idempotencyKey: actionKey(key) }),
    onSuccess: () => {
      resetActionKey(key);
      void client.invalidateQueries({ queryKey: DEMO_ROOT });
    },
  });
}
